import { apiService } from './apiService';

class InstanceService {
  // 인스턴스 목록 조회
  async getInstances(filters = {}) {
    try {
      const response = await apiService.get('/instances', filters);
      
      if (response.success) {
        return response.instances || [];
      } else {
        throw new Error(response.error || '인스턴스 목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('인스턴스 목록 조회 오류:', error);
      throw error;
    }
  }

  // 특정 인스턴스 조회
  async getInstance(instanceId) {
    try {
      const response = await apiService.get(`/instances/${instanceId}`);
      
      if (response.success) {
        return response.instance;
      } else {
        throw new Error(response.error || '인스턴스 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('인스턴스 조회 오류:', error);
      throw error;
    }
  }

  // 인스턴스 제어 (start, stop, reboot, terminate)
  async controlInstance(instanceId, action) {
    try {
      const response = await apiService.post(`/instances/${instanceId}/control`, {
        action: action
      });
      
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || `${action} 작업에 실패했습니다.`);
      }
    } catch (error) {
      console.error(`인스턴스 ${action} 오류:`, error);
      throw error;
    }
  }

  // 인스턴스 시작
  async startInstance(instanceId) {
    return this.controlInstance(instanceId, 'start');
  }

  // 인스턴스 중지
  async stopInstance(instanceId) {
    return this.controlInstance(instanceId, 'stop');
  }

  // 인스턴스 재시작
  async rebootInstance(instanceId) {
    return this.controlInstance(instanceId, 'reboot');
  }

  // 인스턴스 종료
  async terminateInstance(instanceId) {
    return this.controlInstance(instanceId, 'terminate');
  }

  // 태그 관리
  async manageTags(instanceId, action, tags = {}, tagKeys = []) {
    try {
      const requestData = {
        action: action,
        tags: tags,
        tagKeys: tagKeys
      };

      const response = await apiService.post(`/instances/${instanceId}/tags`, requestData);
      
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || '태그 관리에 실패했습니다.');
      }
    } catch (error) {
      console.error('태그 관리 오류:', error);
      throw error;
    }
  }

  // 태그 추가/업데이트
  async addTags(instanceId, tags) {
    return this.manageTags(instanceId, 'add', tags);
  }

  // 태그 제거
  async removeTags(instanceId, tagKeys) {
    return this.manageTags(instanceId, 'remove', {}, tagKeys);
  }

  // 스케줄 설정
  async setSchedule(instanceId, schedule) {
    try {
      const response = await apiService.post(`/instances/${instanceId}/schedule`, {
        schedule: schedule
      });
      
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || '스케줄 설정에 실패했습니다.');
      }
    } catch (error) {
      console.error('스케줄 설정 오류:', error);
      throw error;
    }
  }

  // 인스턴스 통계 조회
  async getInstanceStats(filters = {}) {
    try {
      const instances = await this.getInstances(filters);
      
      const stats = {
        total: instances.length,
        running: instances.filter(i => i.state === 'running').length,
        stopped: instances.filter(i => i.state === 'stopped').length,
        pending: instances.filter(i => i.state === 'pending').length,
        stopping: instances.filter(i => i.state === 'stopping').length,
        terminated: instances.filter(i => i.state === 'terminated').length,
        other: instances.filter(i => !['running', 'stopped', 'pending', 'stopping', 'terminated'].includes(i.state)).length
      };

      return stats;
    } catch (error) {
      console.error('인스턴스 통계 조회 오류:', error);
      throw error;
    }
  }

  // 환경별 인스턴스 조회
  async getInstancesByEnvironment(environment) {
    return this.getInstances({ environment: environment });
  }

  // 프로젝트별 인스턴스 조회
  async getInstancesByProject(project) {
    return this.getInstances({ project: project });
  }

  // 상태별 인스턴스 조회
  async getInstancesByState(state) {
    return this.getInstances({ state: state });
  }

  // 일괄 작업
  async bulkAction(instanceIds, action) {
    try {
      const promises = instanceIds.map(instanceId => 
        this.controlInstance(instanceId, action)
      );

      const results = await Promise.allSettled(promises);
      
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return {
        success: true,
        total: instanceIds.length,
        successful: successful,
        failed: failed,
        results: results
      };
    } catch (error) {
      console.error('일괄 작업 오류:', error);
      throw error;
    }
  }

  // 인스턴스 히스토리 조회
  async getInstanceHistory(instanceId, options = {}) {
    try {
      const { limit = 50, type = 'all' } = options;
      const queryParams = { limit, type };
      
      const response = await apiService.get(`/instances/${instanceId}/history`, queryParams);
      
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || '인스턴스 히스토리 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('인스턴스 히스토리 조회 오류:', error);
      throw error;
    }
  }

  // 인스턴스 상세 정보 조회 (기본 정보 + 히스토리)
  async getInstanceDetails(instanceId) {
    try {
      const [instanceInfo, historyData] = await Promise.all([
        this.getInstance(instanceId),
        this.getInstanceHistory(instanceId, { limit: 10 })
      ]);

      return {
        instance: instanceInfo,
        history: historyData.history,
        stats: historyData.stats,
        recentAction: historyData.recentAction,
        userStats: historyData.userStats
      };
    } catch (error) {
      console.error('인스턴스 상세 정보 조회 오류:', error);
      throw error;
    }
  }

  // 필터링을 위한 고유값 조회
  async getFilterOptions() {
    try {
      const instances = await this.getInstances();
      
      const environments = [...new Set(instances
        .map(i => i.tags?.Environment)
        .filter(Boolean)
      )].sort();
      
      const projects = [...new Set(instances
        .map(i => i.tags?.Project)
        .filter(Boolean)
      )].sort();
      
      const owners = [...new Set(instances
        .map(i => i.tags?.Owner)
        .filter(Boolean)
      )].sort();
      
      const states = [...new Set(instances
        .map(i => i.state)
        .filter(Boolean)
      )].sort();

      return {
        environments,
        projects,
        owners,
        states
      };
    } catch (error) {
      console.error('필터 옵션 조회 오류:', error);
      throw error;
    }
  }
}

export const instanceService = new InstanceService();
export default instanceService;